import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    DollarSign, Search, Filter, ChevronDown, ChevronUp,
    Calendar, Download, FileText, CreditCard, Building2,
    ChevronLeft, ChevronRight, Menu, TrendingUp, AlertCircle,
    Receipt, Clock, ArrowUpRight, ArrowDownRight, CheckCircle,
    Wallet, Banknote, PieChart, TruckIcon, Package, Eye, Upload, Users, X, MapPin, XCircle, Loader2
  } from 'lucide-react';
import api from '../utils/api';
import DocumentCard from './DocumentCard';
import { getLoadDisplayId } from '../utils/loadUtils';

const STATUS_CONFIG = {
    load_completed: {
      icon: CheckCircle,
      label: 'Load Completed',
      color: 'gray'
    },
    invoice_uploaded: {
      icon: FileText,
      label: 'Invoice Uploaded',
      color: 'blue'
    },
    charges_approved: {
      icon: CheckCircle,
      label: 'Charges Approved',
      color: 'green'
    },
    charges_disputed: {
      icon: AlertCircle,
      label: 'Disputed',
      color: 'red'
    },
    under_review: {
      icon: Clock,
      label: 'Under Review',
      color: 'yellow'
    },
    awaiting_payment: {
      icon: Clock,
      label: 'Awaiting Payment',
      color: 'yellow'
    },
    payment_marked: {  // This was missing!
      icon: DollarSign,
      label: 'Payment Marked',
      color: 'green'
    },
    payment_verified: {
      icon: CheckCircle,
      label: 'Payment Verified',
      color: 'green'
    },
    released_to_carrier: {
      icon: TruckIcon,
      label: 'Released',
      color: 'green'
    },
    dispute_resolved: {
      icon: CheckCircle,
      label: 'Resolved',
      color: 'green'
    }
};

const StatusBadge = ({ status, compact = false }) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;
    
    const colorVariants = {
      green: 'bg-green-50 text-green-700 border-green-200',
      yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      red: 'bg-red-50 text-red-700 border-red-200',
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      gray: 'bg-gray-50 text-gray-700 border-gray-200'
    };
  
    return (
      <div className={`
        ${compact ? 'px-2 py-0.5' : 'px-2.5 py-1'}
        rounded-full
        border
        flex items-center gap-1
        w-fit
        ${colorVariants[config.color] || colorVariants.gray}
      `}>
        <Icon className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
        <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-medium`}>{config.label}</span>
      </div>
    );
};

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const DetailCard = ({ icon: Icon, label, value }) => (
    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value}</p>
      </div>
    </div>
);

const MobileInvoiceItem = ({ invoice, onAccept, onDispute, nextActions, onRefresh, handleAction }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showFullDetails, setShowFullDetails] = useState(false);
    const [documents, setDocuments] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const user = useSelector((state) => state.auth.user);
    const isCarrier = user?.role?.startsWith('carrier');
  
    const fetchDocuments = async () => {
      if (!showFullDetails || documents) return;
      
      try {
        setIsLoading(true);
        const response = await api.get(`/api/loads/${invoice.id}/documents`);
        setDocuments(response.data.documents);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoading(false);
      }
    };
  
    useEffect(() => {
      fetchDocuments();
    }, [showFullDetails]);
  
    const handleViewDetails = (e) => {
      e.stopPropagation(); // Prevent toggling the basic expansion
      setShowFullDetails(true);
    };
  
    const needsDisputeDocuments = () => {
      return isCarrier && 
             invoice.status === 'under_review' && 
             (!documents?.dispute_documents || documents.dispute_documents.length === 0);
    };
  
    return (
      <motion.div
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="p-4 hover:bg-gray-50"
      >
        {!showFullDetails ? (
          // Basic view with summary
          <>
            <div 
              className="flex justify-between items-start cursor-pointer"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <div className="flex items-start space-x-2">
                <div className="mt-0.5 w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-4 h-4 text-blue-500" />
                </div>
                <div className="min-w-0 flex-1">
                  {/* Load ID and Status - Stacked on mobile */}
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-mono font-medium text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded w-fit">
                      #{getLoadDisplayId(invoice)}
                    </span>
                    <StatusBadge status={invoice.status} compact />
                  </div>
                  <p className="text-xs text-gray-600 mt-1.5 truncate">
                    {invoice.customer_name}
                  </p>
                  <div className="mt-1">
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(invoice.final_rate)}
                    </p>
                    {invoice.base_rate !== invoice.final_rate && (
                      <p className="text-[10px] text-gray-500">
                        Base: {formatCurrency(invoice.base_rate)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <button className="p-1 flex-shrink-0">
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
            </div>
  
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <DetailCard
                      icon={Calendar}
                      label="Due Date"
                      value={new Date(invoice.dueDate).toLocaleDateString()}
                    />
                    <DetailCard
                      icon={Building2}
                      label="Route"
                      value={`${invoice.origin_city} → ${invoice.destination_city}`}
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-2 mt-4">
                    <button 
                      onClick={handleViewDetails}
                      className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700"
                    >
                      View Details
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          // Full details view
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Header with back button */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setShowFullDetails(false)}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Back
              </button>
              <StatusBadge status={invoice.status} />
            </div>
  
            {/* Invoice Details */}
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h3 className="text-lg font-semibold mb-4">Invoice Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailCard
                    icon={Receipt}
                    label="Load ID"
                    value={`#${getLoadDisplayId(invoice)}`}
                  />
                  <DetailCard
                    icon={Calendar}
                    label="Due Date"
                    value={new Date(invoice.dueDate).toLocaleDateString()}
                  />
                  <DetailCard
                    icon={DollarSign}
                    label="Final Rate"
                    value={formatCurrency(invoice.final_rate)}
                  />
                  <DetailCard
                    icon={DollarSign}
                    label="Base Rate"
                    value={formatCurrency(invoice.base_rate)}
                  />
                  {invoice.base_rate !== invoice.final_rate && (
                    <DetailCard
                      icon={TrendingUp}
                      label="Adjustments"
                      value={formatCurrency(invoice.final_rate - invoice.base_rate)}
                    />
                  )}
                  <DetailCard
                    icon={Building2}
                    label="Customer"
                    value={invoice.customer_name}
                  />
                </div>
              </div>
  
              {/* Route Information */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h3 className="text-lg font-semibold mb-4">Route Details</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <MapPin className="w-5 h-5" />
                    <span>{invoice.origin_city}</span>
                    <ChevronRight className="w-5 h-5" />
                    <span>{invoice.destination_city}</span>
                  </div>
                </div>
              </div>
  
              {/* Documents Section */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Documents</h3>
                </div>
                {isLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : documents ? (
                  <div className="space-y-3">
                    {documents.load_documents?.map((doc) => (
                      <DocumentCard 
                        key={doc.id} 
                        document={doc}
                        isMobile={true}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No documents available</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    );
};

export default MobileInvoiceItem; 